// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators use this dashboard to compare recorded spend by
 * scope, year, service, and workspace policy. All monetary figures come from
 * backend rollups; this component must not estimate or invent spend. Show the
 * current-month total, delta, and highlight only for the current calendar year,
 * because applying that label to a past year would misstate its completed data.
 */
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CostsTable } from './CostsTable'
import { CostSummaryCard } from './CostSummaryCard'
import { WorkspaceCostPolicyCard } from './WorkspaceCostPolicyCard'
import { YearSelect } from './YearSelect'
import { useCosts } from '../../hooks/useCosts'
import { Alert, AlertDescription, SegmentedTabs } from '@/components/ui'
import { AuthErrorAlert } from '../ui/AuthErrorAlert'
import { AlertTriangle } from 'lucide-react'
import type { CostLevel } from '../../services/costs-api'
import type { CostRollup } from '../../types'

const formatUSD = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

const sumMonthCosts = (costs: Record<string, CostRollup[]>, month: number): number =>
  Object.values(costs).reduce((sum, serviceCosts) => {
    if (!Array.isArray(serviceCosts)) return sum
    const monthCost = serviceCosts.find(c => c.month === month)
    return sum + (monthCost ? parseFloat(monthCost.total_usd) : 0)
  }, 0)

interface CostsComponentProps {
  organization_id: string
  workspace_id: string
  app_id: string
  defaultCostView: CostLevel
  isGlobalAdmin: boolean
  canManageWorkspaceCosts: boolean
}

export function CostsComponent({
  organization_id,
  workspace_id,
  app_id,
  defaultCostView,
  isGlobalAdmin,
  canManageWorkspaceCosts,
}: CostsComponentProps) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  const getDefaultTab = (): CostLevel => {
    if (!isGlobalAdmin && (defaultCostView === 'app' || defaultCostView === 'org')) {
      return 'workspace'
    }
    return defaultCostView
  }

  const isValidTab = (tab: string | null): tab is CostLevel => {
    if (!tab) return false
    if (!isGlobalAdmin && (tab === 'app' || tab === 'org')) return false
    return ['app', 'org', 'workspace'].includes(tab)
  }

  // URL-synced tab state with internal state for immediate UI response
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTab = searchParams.get('view')
  const [internalTab, setInternalTab] = useState<CostLevel>(isValidTab(urlTab) ? urlTab : getDefaultTab())
  const activeTab = isValidTab(urlTab) ? urlTab : internalTab

  const handleTabChange = useCallback((value: CostLevel) => {
    if (!isGlobalAdmin && (value === 'app' || value === 'org')) {
      return
    }
    setInternalTab(value)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('view', value)
      return next
    }, { replace: true })
  }, [isGlobalAdmin, setSearchParams])

  const { costs, isLoading, error } = useCosts(
    selectedYear,
    app_id,
    organization_id,
    workspace_id,
    activeTab,
  )

  const monthSums = useMemo(
    () => Array.from({ length: 12 }, (_, i) => sumMonthCosts(costs, i + 1)),
    [costs],
  )

  const currentMonth = selectedYear === new Date().getFullYear() ? new Date().getMonth() + 1 : null

  const lastUpdated = useMemo(() => {
    const timestamps = Object.values(costs)
      .filter(Array.isArray)
      .flat()
      .map(rollup => rollup.updated_at)
      .filter(Boolean)
    if (timestamps.length === 0) return null
    const latest = timestamps.reduce((a, b) => (a > b ? a : b))
    return new Date(latest).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }, [costs])

  const tabs: { id: CostLevel; label: string }[] = [
    ...(isGlobalAdmin ? [{ id: 'app' as const, label: 'Global App' }, { id: 'org' as const, label: 'Organization' }] : []),
    { id: 'workspace', label: 'Workspace' },
  ]

  const scopeTitle = activeTab === 'app' ? 'Global App Costs' : activeTab === 'org' ? 'Organization Costs' : 'Workspace Costs'
  const scopePath = activeTab === 'app' ? app_id : activeTab === 'org' ? organization_id : `${organization_id}/${workspace_id}`

  return (
    <div className="text-slate-900">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Cost Tracking</h1>
          <p className="mt-[5px] text-[13px] text-slate-500">Track and monitor AI service usage costs</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <SegmentedTabs tabs={tabs} value={activeTab} onChange={handleTabChange} />
          <YearSelect value={selectedYear} onChange={setSelectedYear} />
        </div>
      </div>

      <div className="mt-7 mb-3.5 flex flex-wrap items-center gap-2.5">
        <span className="text-sm font-semibold tracking-[-0.01em]">{scopeTitle}</span>
        <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600 break-all">
          {scopePath}
        </span>
        <span className="flex-1" />
        {lastUpdated && <span className="text-xs text-slate-400">Last updated {lastUpdated}</span>}
      </div>

      <AuthErrorAlert />
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <CostSummaryCard
        year={selectedYear}
        monthSums={monthSums}
        serviceCount={Object.keys(costs).length}
        currentMonth={currentMonth}
        formatUSD={formatUSD}
      />

      {activeTab === 'workspace' && (
        <WorkspaceCostPolicyCard
          organizationId={organization_id}
          workspaceId={workspace_id}
          canManage={canManageWorkspaceCosts}
        />
      )}

      <CostsTable
        costs={costs}
        isLoading={isLoading}
        year={selectedYear}
        currentMonth={currentMonth}
      />
    </div>
  )
}
